import useNavigationStore from '@src/stores/navigationStore';
import useRegistrationStore from '@src/stores/registrationStore';
import styles from './CompagyInformations.module.scss';

import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import BackButton from '@src/components/_shared/BackButton';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import { Form, Formik } from 'formik';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';
import Input from '@src/components/_shared/UI/Input';
import DropdownList from '@src/components/_shared/UI/DropdownList';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getReferencialData } from '@src/api/Referencial';
import CountryDropdown from '@src/components/_shared/UI/CountryDropdownList';
import Loader from '@src/components/_shared/UI/Loader';
import { useEffect, useState } from 'react';
import { companyAdress, companyCreation, companyUpdate, openAccountDemand, personUpdate, updateCompanyAddress } from '@src/api/Registration';
import { compagnyInformationsSchema } from '@src/utilities/schemas';
import { AddressEnumType, AddressType } from '@src/utilities/types';
import { formatDate, handleApiError } from '@src/utilities/functions';

export default function CompanyInformations() {
    const { setSection, setBlock } = useNavigationStore();
    const { setField, registration } = useRegistrationStore();
    const [submitted, setSubmitted] = useState<boolean>(false);

    const { data, isLoading } = useQuery({
        queryKey: ['combinedReferencialData'],
        queryFn: async () => {
            const [legalFormsData, countriesData ] = await Promise.all([
                getReferencialData({type: 'company/legal-forms'}),
                getReferencialData({type: 'countries'})
            ]);
            return { legalFormsData, countriesData };
        }
    });

    const { mutate: updateCompanyAddresses, isPending: isUpdatePending } = useMutation({
        mutationFn: async () => {
            await updateCompanyAddress({
                addressValue: registration.company_addresses.find(
                    address => address.address_type === AddressEnumType.postale
                ) as AddressType,
                demand_id: registration.demand_id,
                is_psi: registration.partner_informations.is_partner,
                person_id: registration.person_id
            });
            await updateCompanyAddress({
                addressValue: registration.company_addresses.find(
                    address => address.address_type === AddressEnumType.fiscale
                ) as AddressType,
                demand_id: registration.demand_id,
                is_psi: registration.partner_informations.is_partner,
                person_id: registration.person_id
            });
        },
        onSuccess: () => {
            setBlock('Various');
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate: mutateCompanyAddresses, isPending: isMutateCompanyAddressesPending } = useMutation({
        mutationFn: async () => {
            const postaleResponse = await companyAdress({
                addressValue: registration.company_addresses.find(
                    address => address.address_type === AddressEnumType.postale
                ) as AddressType,
                demand_id: registration.demand_id,
                is_psi: registration.partner_informations.is_partner,
                person_id: registration.person_id
            });
            registration.company_addresses.filter((obj) => {
                return obj.address_type === postaleResponse.type ? (obj.id = postaleResponse.id) : null;
            });
            const fiscaleResponse = await companyAdress({
                demand_id: registration.demand_id,
                addressValue: registration.company_addresses.find(
                    address => address.address_type === AddressEnumType.fiscale
                ) as AddressType,
                is_psi: registration.partner_informations.is_partner,
                person_id: registration.person_id
            });
            registration.company_addresses.filter((obj) => {
                return obj.address_type === fiscaleResponse.type ? (obj.id = fiscaleResponse.id) : null;
            });
        },
        onSuccess: () => {
            setBlock('Various');
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate: mutateKnownBy, isPending: isMutateKnownByPending } = useMutation({
        mutationFn: (demandId: string) => personUpdate({key: 'channel', value: {known_by: registration.person.channel.known_by}, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id, demand_id: demandId}),
        onSettled: () => mutateCompanyAddresses(),
        onError: (error: any) => handleApiError(error)
    });

    const { mutate: mutateOpenAccount, isPending: isMutateOpenAccountPending } = useMutation({
        mutationFn: () => openAccountDemand({id: registration.company.company_id, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id, sitecode: registration.partner_informations.brand.sitecode, manager_id: registration.partner_informations.cgp_id, origin: Object.values(registration.origin).some(Boolean) ? registration.origin : undefined}),
        onSuccess: (data: any) => {
            setField('demand_id', data.demand_id)
            if (!registration.partner_informations.is_partner) {
                mutateKnownBy(data.demand_id)
            } else {
                mutateCompanyAddresses()
            }
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate, isPending } = useMutation({
        mutationFn: () => companyCreation({basic: registration.company.basic, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id}), 
        onSuccess: (data: any) => {
            setField('company.company_id', data.companyId)
            mutateOpenAccount()
        },
        onError: (error: any) => handleApiError(error)
    });

    const { mutate: updateCompany, isPending: isUpdateCompanyPending } = useMutation({
        mutationFn: () => companyUpdate({value: { basic: registration.company.basic }, is_psi: registration.partner_informations.is_partner, person_id: registration.person_id}), 
        onSuccess: () => {
            const postalAddress = registration.company_addresses.find(address => address.address_type === AddressEnumType.postale);
            const fiscalAddress = registration.company_addresses.find(address => address.address_type === AddressEnumType.fiscale);
            if (postalAddress?.id && fiscalAddress?.id) {
                updateCompanyAddresses();
            } else {
                mutateCompanyAddresses();
            }
        },
        onError: (error: any) => handleApiError(error)
    });

    useEffect(() => {
        if (submitted) {
            if (registration.company.company_id) {
                if (registration.demand_id) {
                    updateCompany();
                } else {
                    mutateOpenAccount();
                }
            } else {
                mutate();
            }
        }
        return () => {
            setSubmitted(false)
        }
    }, [submitted])

    const _onGoBack = () => {
        setSection(3)
        setBlock('SirenNumber')
    };

    const titleText = registration.company.basic.name ? "Nous vous avons trouvé !" : "Information sur la société";
    const subTitleText = registration.company.basic.name ? "Nous avons récupéré les données publiques à partir de votre numéro Siren. Veuillez vérifier les informations et les changer si besoin." : "Veuillez renseigner les informations suivantes"

    return (
        <Container>
            <BackButton onClick={_onGoBack}/>
            <BlockTitle title={titleText} />
            <Subtitle content={subTitleText} />
            {isLoading ? 
                <Loader type='SPINNER' />
                :
                <Formik
                    initialValues={{
                        name: registration.company.basic.name,
                        legal_form: registration.company.basic.legal_form,
                        created_at: registration.company.basic.created_at
                            ? (() => {
                                const dateValue = registration.company.basic.created_at;
                                if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
                                    return dateValue;
                                }
                                const date = new Date(dateValue);
                                return isNaN(date.getTime()) ? '' : date.toLocaleDateString('fr-FR');
                            })()
                            : '',
                        rcs: registration.company.basic.rcs,
                        ape: registration.company.basic.ape,
                        siret: registration.company.basic.siret,
                        address: {
                            streetName: registration.company_addresses.find(address => address.address_type === AddressEnumType.postale)?.street_name || '',
                            streetNumber: registration.company_addresses.find(address => address.address_type === AddressEnumType.postale)?.street_number || '',
                            zipCode: registration.company_addresses.find(address => address.address_type === AddressEnumType.postale)?.city.zipcode || '',
                            city: registration.company_addresses.find(address => address.address_type === AddressEnumType.postale)?.city.name || '',
                            country: registration.company_addresses.find(address => address.address_type === AddressEnumType.postale)?.country || 'FR',
                        }
                    }}
                    onSubmit={(values) => {
                        setField('company.basic', {
                            name: values.name,
                            legal_form: values.legal_form,
                            created_at: values.created_at,
                            rcs: values.rcs,
                            ape: values.ape,
                            siret: values.siret,
                        });
                        registration.company_addresses.filter((obj: AddressType) => obj.address_type === AddressEnumType.postale).forEach((obj) => {
                            obj.street_number = values.address.streetNumber;
                            obj.street_name = values.address.streetName;
                            obj.city.zipcode = values.address.zipCode;
                            obj.city.name = values.address.city;
                            obj.country = values.address.country;
                        });
                        registration.company_addresses.filter((obj: AddressType) => obj.address_type === AddressEnumType.fiscale).forEach((obj) => {
                            obj.street_number = values.address.streetNumber;
                            obj.street_name = values.address.streetName;
                            obj.city.zipcode = values.address.zipCode;
                            obj.city.name = values.address.city;
                            obj.country = values.address.country;
                        });
                        setSubmitted(true);
                    }}
                    validationSchema={compagnyInformationsSchema}
                >
                    {({ handleChange, handleSubmit, values, errors, isValid, setFieldValue, touched }) => {
                        return (
                            <Form id="companyForm" autoComplete="off" className="registerForm">
                                <div className={styles.inputGroup}>
                                    <Input
                                        title="Raison sociale (nom commercial)"
                                        name="company_name"
                                        id="company_name"
                                        width="100%"
                                        type="text"
                                        placeholder="Ex: L'Oréal"
                                        value={values.name}
                                        onChange={handleChange('name')}
                                        clearInput={() => handleChange('name')('')}
                                        hasError={(values.name && !!errors.name) as boolean}
                                    />
                                    <DropdownList
                                        title="Forme juridique"
                                        width="100%"
                                        options={data?.legalFormsData}
                                        selected={values.legal_form}
                                        onSelect={(value) => setFieldValue('legal_form', value)}
                                        hasError={!!errors.legal_form && touched.legal_form}
                                        isLimited
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <Input
                                        title="Numéro Siren"
                                        name="siren_code"
                                        id="siren_code"
                                        width="100%"
                                        widthLimit="42%"
                                        type="text"
                                        placeholder="Ex : 987654321"
                                        value={values.rcs}
                                        onChange={handleChange('rcs')}
                                        clearInput={() => handleChange('rcs')('')}
                                        hasError={(values.rcs && !!errors.rcs) as boolean}
                                    />
                                    <Input
                                        title="Numéro Siret"
                                        name="siret_code"
                                        id="siret_code"
                                        width="100%"
                                        widthLimit="58%"
                                        type="text"
                                        placeholder="Ex : 30613890001294"
                                        value={values.siret}
                                        onChange={handleChange('siret')}
                                        clearInput={() => handleChange('siret')('')}
                                        hasError={(values.siret && !!errors.siret) as boolean}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <Input
                                        title="Code APE"
                                        name="ape_code"
                                        id="ape_code"
                                        width="100%"
                                        type="text"
                                        placeholder="Ex : 8411Z"
                                        value={values.ape}
                                        onChange={handleChange('ape')}
                                        clearInput={() => handleChange('ape')('')}
                                        hasError={(values.ape && !!errors.ape) as boolean}
                                    />
                                    <Input
                                        title="Date de création"
                                        name="created_at"
                                        id="created_at"
                                        width="100%"
                                        type="text"
                                        placeholder="JJ/MM/AAAA"
                                        maxLength={10}
                                        value={values.created_at}
                                        onChange={(e) => {
                                            const formattedDate = formatDate(e.target.value);
                                            setFieldValue('created_at', formattedDate);
                                        }}
                                        clearInput={() => handleChange('created_at')('')}
                                        hasError={(values.created_at && !!errors.created_at) as boolean}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <Input
                                        title="N°"
                                        name="street_number"
                                        id="street_number"
                                        width="100%"
                                        widthLimit="120px"
                                        type="text"
                                        placeholder=""
                                        value={values.address.streetNumber}
                                        onChange={handleChange('address.streetNumber')}
                                        clearInput={() => handleChange('address.streetNumber')('')}
                                        hasError={(values.address.streetNumber && !!errors.address?.streetNumber) as boolean}
                                    />
                                    <Input
                                        title="Rue / voie"
                                        name="street_name"
                                        id="street_name"
                                        width="100%"
                                        widthLimit="380px"
                                        type="text"
                                        placeholder="Ex : rue de la colombe"
                                        value={values.address.streetName}
                                        onChange={handleChange('address.streetName')}
                                        clearInput={() => handleChange('address.streetName')('')}
                                        hasError={(values.address.streetName && !!errors.address?.streetName) as boolean}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <Input
                                        title="Code postal"
                                        name="city_zipCode"
                                        id="city_zipCode"
                                        width="100%"
                                        widthLimit="150px"
                                        type="text"
                                        placeholder=""
                                        value={values.address.zipCode}
                                        onChange={handleChange('address.zipCode')}
                                        clearInput={() => handleChange('address.zipCode')('')}
                                        hasError={(values.address.zipCode && !!errors.address?.zipCode) as boolean}
                                    />
                                    <Input
                                        title="Ville"
                                        name="city_name"
                                        id="city_name"
                                        width="100%"
                                        widthLimit="300px"
                                        type="text"
                                        placeholder="Ex: Paris"
                                        value={values.address.city}
                                        onChange={handleChange('address.city')}
                                        clearInput={() => handleChange('address.city')('')}
                                        hasError={(values.address.city && !!errors.address?.city) as boolean}
                                    />
                                </div>
                                <CountryDropdown
                                    title="Pays"
                                    width="100%"
                                    options={data?.countriesData}
                                    selected={values.address.country}
                                    onSelect={(value) => setFieldValue('address.country', value)}
                                />
                                <ValidateButton title="Continuer" disabled={!isValid || isPending || isMutateCompanyAddressesPending || isUpdatePending || isMutateOpenAccountPending || isUpdateCompanyPending || isMutateKnownByPending} onLoading={isPending || isMutateCompanyAddressesPending || isUpdatePending || isMutateOpenAccountPending || isMutateKnownByPending} onClick={handleSubmit} />
                            </Form>
                        )
                    }}
                </Formik>
            }
        </Container>
    );
}
